<template>
  <div class="product-view">
    <!-- Top controls: Back button, Save button, Language buttons -->
    <div class="top-product-controls">
      <!-- Left side controls: back & notes -->
      <div class="left-controls">
        <button class="back-button" @click="goToCatalog">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="icon icon-tabler icon-tabler-arrow-left"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <line x1="5" y1="12" x2="19" y2="12" />
            <line x1="5" y1="12" x2="11" y2="18" />
            <line x1="5" y1="12" x2="11" y2="6" />
          </svg>
          <span>Back to Catalog</span>
        </button>
      </div>

      <!-- Center controls: Save button & language buttons -->
      <div class="center-controls">
        <button
          class="save-button"
          v-if="store.getIsWrite() || store.getIsAdmin()"
          @click="saveChanges"
        >
          Save Changes
        </button>

        <div class="language-buttons">
          <button
            v-for="lang in languages"
            :key="lang"
            :class="{ 'selected-lang': selectedLanguage === lang }"
            @click="setLanguage(lang)"
          >
            {{ lang.charAt(0).toUpperCase() + lang.slice(1) }}
          </button>
        </div>
      </div>

      <!-- Right side controls: Notes, Refresh, Reprocess -->
      <div class="right-controls">
        <v-chip
          :color="statusColor"
          variant="elevated"
          :prepend-icon="statusIcon"
        >
          {{ statusText }}
          <span v-if="formattedDate">: {{ formattedDate }}</span>
          <span v-else>: N/A</span>
        </v-chip>
        <button
          class="notes-button"
          :class="{
            'has-notes':
              currentProductData?.alliumInternalData?.notes &&
              currentProductData.alliumInternalData.notes.trim() !== '',
          }"
          @click="openAddNotePopup"
        >
          Notes
        </button>

        <button class="refresh-button" @click="refreshData">
          Refresh Data
        </button>

        <!-- NEW Reprocess button -->
        <button
          class="reprocess-button"
          v-if="store.getIsWrite() || store.getIsAdmin()"
          @click="openReprocessConfirm"
        >
          Reprocess
        </button>
      </div>
    </div>

    <!-- Notes popup -->
    <div v-if="showAddNotePopup" class="modal-overlay">
      <div class="modal-content">
        <h3>Notes</h3>
        <textarea
          v-model="reviewNote"
          rows="5"
          cols="50"
          placeholder="Type your notes here..."
        ></textarea>
        <div class="modal-buttons">
          <button @click="saveNote">Save Note</button>
          <button @click="closeAddNotePopup">Cancel</button>
        </div>
      </div>
    </div>

    <!-- NEW: Reprocess confirmation popup -->
    <div v-if="showReprocessConfirm" class="modal-overlay">
      <div class="modal-content">
        <v-card flat>
          <v-card-title class="text-h5">
            Reprocess Article {{ currentProductId }}?
          </v-card-title>

          <v-card-text>
            <p class="mb-6">
              This will add the article back to the queue to be processed again.
              Please confirm you'd like to continue.
            </p>

            <v-select
              v-model="selectedReprocessOptions"
              :items="reprocessOptions"
              item-title="text"
              item-value="value"
              label="Refine Reprocessing (Optional)"
              hint="Select parts to focus on. Leave empty for a full reprocess."
              persistent-hint
              multiple
              chips
              closable-chips
              variant="outlined"
              :menu-props="{ zIndex: 10000 }"
            ></v-select>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn variant="text" @click="showReprocessConfirm = false">
              Cancel
            </v-btn>

            <v-btn color="#2c3e50" variant="flat" @click="reprocessProduct">
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>

    <!-- Loading / Not Found states -->
    <div v-if="store.getIsLoading()" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Loading product...</p>
    </div>
    <div v-else-if="!currentProductData" class="loading-state">
      <p>Product not found in {{ store.state.selectedDatabase }}.</p>
    </div>

    <!-- Main content when product is loaded -->
    <div v-else class="images-and-controls">
      <!-- Left side: Images container and variant navigation -->
      <div class="images-container">
        <div class="panel">
          <product-images
            :key="`images-${currentProductId}-${currentVariantId}`"
            :images="currentEnrichedData?.images"
            :thumbnails="currentEnrichedData?.thumbnails"
          />
        </div>

        <div class="page-navigation">
          <button
            @click="previousProduct"
            :disabled="isPreviousDisabled"
            class="nav-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="icon icon-tabler icon-tabler-chevron-left"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path stroke="none" d="M0 0h24V24H0z" fill="none" />
              <polyline points="15 6 9 12 15 18" />
            </svg>
          </button>

          <div class="navigation-text">
            <span>
              <span
                class="process-status"
                :class="statusClass"
                style="margin-bottom: 10px"
              >
                {{ displayedStatus }}
              </span>
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <span v-if="qualityCheckLabel" class="process-status" :class="qualityCheckColor"
                    style="margin-bottom: 10px; margin-left: 5px;" v-bind="props">
                    Quality Check: {{ qualityCheckLabel }}
                  </span>
                </template>

                <dl class="quality-flag-container">
                  <div class="quality-flag-display">
                    <dt>Retrievable Image: </dt>
                    <dd v-if="topLevelFlags.hasRetrievableImage === false"><span class="status-text-red">✘</span></dd>
                    <dd v-else><span class="status-text-green">✔</span></dd> <!-- Value is true OR undefined -->
                  </div>
                  <div class="quality-flag-display">
                    <dt>GPC Mapping: </dt>
                    <dd v-if="topLevelFlags.gpcBackendMappingNotOk === true"><span class="status-text-red">✘</span></dd>
                    <dd v-else><span class="status-text-green">✔</span></dd> <!-- Value is false OR undefined -->
                  </div>
                </dl>
              </v-tooltip>
            </span>
            <span class="article-number-text">
              Article No: {{ currentProductId }}
            </span>
            <div class="variant-number-text">
              Variant No: {{ currentVariantId }}
            </div>
            <!-- EAN if present -->
            <div class="ean-text" v-if="currentVariantData.ean">
              EAN: {{ currentVariantData.ean }}
            </div>
            <!-- Web link with fallback pattern -->
            <div class="web-link">
              <a
                :href="`https://www.manor.ch/p/${currentVariantId}`"
                target="_blank"
              >
                Web Link
              </a>
            </div>
          </div>

          <button
            @click="nextProduct"
            :disabled="isNextDisabled"
            class="nav-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="icon icon-tabler icon-tabler-chevron-right"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              stroke-width="2"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path stroke="none" d="M0 0h24V24H0z" fill="none" />
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        </div>

        <!-- Attribute Filter Form -->
        <div v-if="selectedAttributeFilter.type" class="attribute-filter">
          <label for="attribute-name">Attribute Name:</label>
          <select
            v-model="selectedAttributeFilter.attribute"
            @change="onAttributeNameChange"
            id="attribute-name"
          >
            <option value="">Select Attribute</option>
            <option
              v-for="attrName in attributeFilters[selectedAttributeFilter.type]
                .attributes"
              :key="attrName"
              :value="attrName"
            >
              {{ attrName }}
            </option>
          </select>
        </div>
        <div v-if="selectedAttributeFilter.attribute" class="attribute-filter">
          <label for="attribute-value">Attribute Value:</label>
          <select
            v-model="selectedAttributeFilter.value"
            @change="applyAttributeFilter"
            id="attribute-value"
          >
            <option value="">Select Value</option>
            <option
              v-for="attrValue in attributeFilters[selectedAttributeFilter.type]
                .values[selectedAttributeFilter.attribute]"
              :key="attrValue"
              :value="attrValue"
            >
              {{ attrValue }}
            </option>
          </select>
        </div>

        <!-- Article Search -->
        <div
          class="article-search"
          style="margin-top: 20px; text-align: center"
        >
          <label for="article-search-input">Search Article No:</label>
          <input
            id="article-search-input"
            v-model="articleSearchInput"
            @keyup.enter="onArticleSearch"
            placeholder="Enter Article Number"
          />
          <button @click="onArticleSearch">Go</button>
        </div>

        <!-- Variant Search -->
        <div class="variant-search">
          <label for="variant-search-input">Search Variant No:</label>
          <input
            id="variant-search-input"
            v-model="variantSearchInput"
            @keyup.enter="onVariantSearch"
            placeholder="Enter Variant Number"
          />
          <button @click="onVariantSearch">Go</button>
        </div>

        <!-- EAN Search -->
        <div class="ean-search">
          <label for="ean-search-input">Search EAN:</label>
          <input
            id="ean-search-input"
            v-model="eanSearchInput"
            @keyup.enter="onEanSearch"
            placeholder="Enter EAN"
          />
          <button @click="onEanSearch">Go</button>
        </div>
      </div>

      <!-- Collapse/Expand Button -->
      <button class="collapse-button" @click="toggleSourceSection">>></button>

      <!-- Right side: content container (source + enriched, or collapsed) -->
      <div class="content-container">
        <!-- Expanded view (source + enriched) -->
        <div v-if="!isSourceCollapsed" class="comparison-container">
          <!-- SOURCE Section -->
          <div class="comparison-section">
            <h2 class="section-title">Source</h2>

            <!-- Source Selection Dropdown -->
            <div class="panel source-select">
              <label for="sourceType">Select Source:</label>
              <select id="sourceType" v-model="selectedSourceOption">
                <option v-for="opt in sourceOptions" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>
            </div>

            <!-- Raw "Manor Existing" data -->
            <div v-if="currentSourceDataFound" class="panel">
              <product-details
                :productVariants="currentSourceData?.variants"
                :data="currentSourceData"
                :selectedColor="selectedColor"
                :selectedSize="selectedSize"
                is-after
                :edited-details="editedDetails"
                @detailEdited="handleDetailEdited"
                @colorSelected="selectColor"
                @sizeSelected="selectSize"
                :selectedLanguage="selectedLanguage"
                :highlightGpc="shouldHighlightGpc"
                :is-editable="false"
                :variantData="currentSourceData.variantData || {}"
                :hasVariantData="currentSourceData.hasVariantData || false"
                :currentVariantId="currentSourceData.currentVariantId || ''"
              />
            </div>
            <div v-else class="panel no-source">
              <h3>
                No Source Found for
                {{ store.state.selectedDatabase }} environment
              </h3>
            </div>

            <!-- Source Product Attributes -->
            <div v-if="currentSourceDataFound" class="panel">
              <product-attributes
                header="Product Attributes"
                :originalAttributes="currentSourceData.productAttributes"
                :attributesSources="currentSourceData.attributesSources"
                :editedAttributes="{}"
                :flaggedAttributes="{}"
                :attributesMatchingDefault="{}"
                :selectedLanguage="selectedLanguage"
                sourceName="Manor Existing"
              />
            </div>
            <div v-if="currentSourceDataFound" class="panel">
              <product-attributes
                header="Variant Attributes"
                :originalAttributes="currentSourceData.variantAttributes"
                :attributesSources="currentSourceData.attributesSources"
                :editedAttributes="{}"
                :flaggedAttributes="{}"
                :attributesMatchingDefault="{}"
                :selectedLanguage="selectedLanguage"
                sourceName="Manor Existing (Variant)"
                :isVariantAttributes="true"
              />
            </div>
          </div>

          <!-- ENRICHED Section -->
          <div class="comparison-section">
            <h2 class="section-title">Enriched</h2>

            <div class="panel">
              <product-details
                :data="currentEnrichedData"
                :productVariants="currentEnrichedData?.variants"
                :selectedColor="selectedColor"
                :selectedSize="selectedSize"
                is-after
                :edited-details="editedDetails"
                @detailEdited="handleDetailEdited"
                @colorSelected="selectColor"
                @sizeSelected="selectSize"
                :selectedLanguage="selectedLanguage"
                :highlightGpc="shouldHighlightGpc"
                :is-editable="true"
                :variantData="currentEnrichedData.variantData"
                :hasVariantData="currentEnrichedData.hasVariantData"
                :currentVariantId="currentEnrichedData.currentVariantId"
              />
            </div>

            <div class="panel">
              <product-attributes
                header="Product Attributes"
                :originalAttributes="currentEnrichedData.productAttributes"
                :editedAttributes="editedProductAttributes"
                :attributesSources="currentEnrichedData.attributesSources"
                :flaggedAttributes="{}"
                :attributesMatchingDefault="
                  currentEnrichedData.attributesMatchingDefault
                "
                @attributeEdited="handleProductAttributeEdited"
                @attributeDeleted="handleProductAttributeDeleted"
                :selectedLanguage="selectedLanguage"
                sourceName="Enriched"
              />
            </div>

            <div class="panel">
              <product-attributes
                header="Variant Attributes"
                :originalAttributes="currentEnrichedData.variantAttributes"
                :editedAttributes="editedVariantAttributes"
                :attributesSources="currentEnrichedData.attributesSources"
                :flaggedAttributes="
                  currentProductData.alliumInternalData?.processingDetails
                    ?.processingFlags?.attributesFlags || {}
                "
                :attributesMatchingDefault="
                  currentEnrichedData.attributesMatchingDefault
                "
                @attributeEdited="handleVariantAttributeEdited"
                @attributeDeleted="handleVariantAttributeDeleted"
                :selectedLanguage="selectedLanguage"
                sourceName="Enriched (Variant)"
                :isVariantAttributes="true"
              />
            </div>
          </div>
        </div>

        <!-- Collapsed view (enriched only) -->
        <div v-else class="two-pane-container">
          <!-- Left Pane -->
          <div class="left-pane">
            <product-details
              :productVariants="currentEnrichedData?.variants"
              :data="currentEnrichedData"
              :selectedColor="selectedColor"
              :selectedSize="selectedSize"
              is-after
              :edited-details="editedDetails"
              @detailEdited="handleDetailEdited"
              @colorSelected="selectColor"
              @sizeSelected="selectSize"
              :selectedLanguage="selectedLanguage"
              :highlightGpc="shouldHighlightGpc"
              :is-editable="true"
              :variantData="currentEnrichedData?.variantData"
              :hasVariantData="currentEnrichedData?.hasVariantData"
              :currentVariantId="currentEnrichedData?.currentVariantId"
            />
          </div>

          <!-- Right Pane -->
          <div class="right-pane">
            <product-attributes
              header="Product Attributes"
              :originalAttributes="currentEnrichedData?.productAttributes"
              :editedAttributes="editedProductAttributes"
              :attributesSources="currentEnrichedData?.attributesSources"
              :flaggedAttributes="{}"
              :attributesMatchingDefault="
                currentEnrichedData?.attributesMatchingDefault
              "
              @attributeEdited="handleProductAttributeEdited"
              @attributeDeleted="handleProductAttributeDeleted"
              :selectedLanguage="selectedLanguage"
              sourceName="Enriched"
            />

            <product-attributes
              header="Variant Attributes"
              :originalAttributes="currentEnrichedData?.variantAttributes"
              :editedAttributes="editedVariantAttributes"
              :attributesSources="currentEnrichedData?.attributesSources"
              :flaggedAttributes="currentEnrichedData?.alliumFlaggedAttributes"
              :attributesMatchingDefault="
                currentEnrichedData?.attributesMatchingDefault
              "
              @attributeEdited="handleVariantAttributeEdited"
              @attributeDeleted="handleVariantAttributeDeleted"
              :selectedLanguage="selectedLanguage"
              sourceName="Enriched (Variant)"
              :isVariantAttributes="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Snackbar for save feedback -->
    <div v-if="showSnackbar" :class="['snackbar', snackbarColor]">
      {{ snackbarMessage }}
    </div>
  </div>
</template>

<script>
import { auth } from "@/firebase";
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useProductStore } from '@/store/product-viewer/productStore';
import { productService } from '@/services/product-viewer/productService';
import ProductImages from "@/components/product-viewer/ProductImages.vue";
import ProductDetails from "@/components/product-viewer/ProductDetails.vue";
import ProductAttributes from "@/components/product-viewer/ProductAttributes.vue";
import { normalizeHexColor } from "@/productviewerutils";
import dayjs from "dayjs";

export default {
  name: "ProductView",
  components: {
    ProductImages,
    ProductDetails,
    ProductAttributes,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const store = useProductStore();
    const router = useRouter();
    const route = useRoute();

    // UI state
    const showAddNotePopup = ref(false);
    const showSnackbar = ref(false);
    const snackbarMessage = ref("");
    const snackbarColor = ref("success");
    const reviewNote = ref("");
    const selectedLanguage = ref("english"); // 'english','german','french','italian'
    const isSourceCollapsed = ref(true);

    // Reprocess confirm popup
    const showReprocessConfirm = ref(false);

    // Product state
    const selectedVariantId = ref("");
    const selectedColor = ref("");
    const selectedSize = ref("");
    const editedProductAttributes = ref({});
    const editedVariantAttributes = ref({});
    const editedDetails = ref({});

    // Computed properties for status display
    const statusColor = computed(() => {
      const status =
        currentProductData.value?.alliumInternalData?.processStatus?.toLowerCase() ||
        "";
      if (status === "completed") return "success"; // green
      if (status === "processing") return "warning"; // yellow
      return "error"; // red for N/A or other states
    });

    const statusIcon = computed(() => {
      const status =
        currentProductData.value?.alliumInternalData?.processStatus?.toLowerCase() ||
        "";
      if (status === "completed") return "mdi-check-circle";
      if (status === "processing") return "mdi-clock-outline";
      return "mdi-alert-circle";
    });

    const statusText = computed(() => {
      const status =
        currentProductData.value?.alliumInternalData?.processStatus ||
        "Unknown";
      return status.charAt(0).toUpperCase() + status.slice(1);
    });

    const topLevelFlags = computed(() => {
      return currentProductData.value?.alliumInternalData?.processingDetails?.processingFlags || {};
    });

    const formattedDate = computed(() => {
      let dateStr =
        currentProductData.value?.alliumInternalData?.processCompleteDate;

      if (!dateStr) return null;

      // Fix invalid date format: remove the 'Z' if there's already a timezone offset like '+00:00'
      if (dateStr.includes("+") && dateStr.endsWith("Z")) {
        dateStr = dateStr.slice(0, -1);
      }

      return dayjs(dateStr).format("MMM D, YYYY h:mm A");
    });
    // Source options
    const sourceOptions = ["Vendor", "Manor Existing", "External"];
    const selectedSourceOption = ref("Manor Existing");
    const reprocessOptions = ref([
      { value: "gpc_categorization", text: "GPC Categorization" },
      { value: "backend_categorization", text: "Backend Categorization" },
      { value: "frontend_categorization", text: "Frontend Categorization" },
      { value: "attributes", text: "Product Attributes" },
      { value: "text_fields", text: "Text Fields (Description, etc.)" },
      { value: "colors", text: "Colors" },
      { value: "size", text: "Sizing" },
      { value: "variant_attributes", text: "Variant Attributes" },
    ]);

    // Holds the array of selected reprocessing options
    const selectedReprocessOptions = ref([]);
    const manorExistingCache = ref({});
    const attributeTaxonomies = ref([]);

    // Filter state
    const selectedAttributeFilter = reactive({
      type: "",
      attribute: "",
      value: "",
    });
    const attributeFilters = reactive({
      dressCategory: { attributes: [], values: {} },
      manorTaxonomy: { attributes: [], values: {} },
      alliumTaxonomy: { attributes: [], values: {} },
    });

    // Search inputs
    const articleSearchInput = ref("");
    const variantSearchInput = ref("");
    const eanSearchInput = ref("");

    // Language helper
    const mapLang = {
      english: "EN",
      german: "DE",
      french: "FR",
      italian: "IT",
    };

    // A small helper to fetch a property ignoring case
    function getCaseInsensitive(obj, lang) {
      if (!obj || typeof obj !== "object") return undefined;
      const foundKey = Object.keys(obj).find(
        (k) => k.toLowerCase() === lang.toLowerCase()
      );
      return foundKey ? obj[foundKey] : undefined;
    }

    const currentProductId = computed(() => props.id);

    // Normalizing / fallback for color HEX codes

    // ----- GETTERS ---------------
    const currentProductData = computed(() =>
      store.getProduct(currentProductId.value)
    );
    const currentProductVariants = computed(
      () => currentProductData.value?.variants || []
    );
    const variantIds = computed(() =>
      currentProductVariants.value.map((v) => v.id)
    );

    const qualityCheckColor = computed(() => {
      return {
        "OK": "status-green",
        "REVIEW": "status-orange",
        "NOT_OK": "status-red",
      }[currentProductData?.value?.processingDetails?.qualityFlag];
    });

    const qualityCheckLabel = computed(() => {
      return {
        "OK": "Okay",
        "REVIEW": "Needs Review",
        "NOT_OK": "Not Okay",
      }[currentProductData?.value?.processingDetails?.qualityFlag];
    });

    const shouldHighlightGpc = computed(() => {
      const details =
        currentProductData.value?.alliumInternalData?.processingDetails;
      // highlight if `gpcBrickUnchanged` is explicitly false
      return details ? details.gpcBrickUnchanged === false : false;
    });

    const currentVariantId = computed(() => {
      if (selectedVariantId.value) return selectedVariantId.value;
      if (variantIds.value.length > 0) return variantIds.value[0];
      return "";
    });

    const currentVariantData = computed(() => {
      return (
        currentProductVariants.value.find(
          (x) => x.id === currentVariantId.value
        ) || {}
      );
    });

    // The "Manor Existing" raw data
    const currentSourceData = computed(() => {
      if (selectedSourceOption.value !== "Manor Existing") {
        return emptyProductData();
      }
      if (!currentProductId.value) {
        return emptyProductData();
      }
      const rawData = manorExistingCache.value[currentProductId.value];
      if (!rawData) {
        return emptyProductData();
      }
      let rawVariant = {};
      if (Array.isArray(rawData.variants)) {
        rawVariant =
          rawData.variants.find((v) => v.id === currentVariantId.value) ||
          rawData.variants[0] ||
          {};
      }
      return mapRawProductData(rawData, rawVariant);
    });
    const currentSourceDataFound = computed(() => {
      return (
        !!currentSourceData.value &&
        currentSourceData.value.title !== "No title available"
      );
    });

    // Enriched data
    const currentEnrichedData = computed(() => {
      const productData = currentProductData.value || emptyProductData();
      const variantData = currentVariantData.value || emptyProductData();;
      const userLang = mapLang[selectedLanguage.value] || "EN";

      // Title
      const rawTitle = getCaseInsensitive(productData.title, userLang);
      const fallbackTitle =
        getCaseInsensitive(productData.title, "EN") ||
        getCaseInsensitive(productData.title, "DE") ||
        getCaseInsensitive(productData.title, "FR") ||
        getCaseInsensitive(productData.title, "IT") ||
        "No title available";
      const titleInLang = rawTitle || fallbackTitle;

      // Model Name
      const rawModelName = getCaseInsensitive(productData.modelName, userLang);
      const fallbackModelName =
        getCaseInsensitive(productData.modelName, "EN") ||
        getCaseInsensitive(productData.modelName, "DE") ||
        getCaseInsensitive(productData.modelName, "FR") ||
        getCaseInsensitive(productData.modelName, "IT") ||
        "";
      const modelNameInLang = rawModelName || fallbackModelName;

      // Product-level description
      const rawDesc = getCaseInsensitive(productData.description, userLang);
      const fallbackDesc =
        getCaseInsensitive(productData.description, "EN") ||
        getCaseInsensitive(productData.description, "DE") ||
        getCaseInsensitive(productData.description, "FR") ||
        getCaseInsensitive(productData.description, "IT") ||
        "No description available";
      const descInLang = rawDesc || fallbackDesc;

      // Variant-level description (separate from product)
      const variantDesc = getCaseInsensitive(variantData.description, userLang);

      // Product-level bullet points
      const productBulletPoints = [];
      if (Array.isArray(productData.bulletPoints)) {
        for (const point of productData.bulletPoints) {
          const translatedText = point.text?.[userLang];
          if (translatedText) {
            productBulletPoints.push(translatedText);
          }
        }
      }

      // Variant-level bulletPoints (separate from product)
      const variantBulletPoints = [];
      if (Array.isArray(variantData.bulletPoints) && variantData.bulletPoints.length > 0) {
        for (const point of variantData.bulletPoints) {
          const translatedText = point.text?.[userLang];
          if (translatedText) {
            variantBulletPoints.push(translatedText);
          }
        }
      }

      // Color / size
      const colorData = variantData.variantDiscriminators?.color || {};
      const sizeData = variantData.variantDiscriminators?.size || {};

      const allColorsArray = [];
      if (Array.isArray(colorData.attrValues)) {
        for (const colorObj of colorData.attrValues) {
          let colorLabel = colorObj.value || "Unknown";
          const cLabelRaw = getCaseInsensitive(colorObj.valueLabels, userLang);
          if (cLabelRaw) {
            colorLabel = cLabelRaw;
          }
          allColorsArray.push(colorLabel);
        }
      }

      let primaryColorLabel = colorData.colorCode || "";
      if (Array.isArray(colorData.attrValues) && colorData.attrValues[0]) {
        const firstVal = colorData.attrValues[0];
        const cLabel = getCaseInsensitive(firstVal.valueLabels, userLang);
        primaryColorLabel = cLabel || firstVal.value || primaryColorLabel;
      }
      const rawPrimaryColorHex = colorData.rgbColor || null;
      const normalizedPrimaryColorHex = normalizeHexColor(rawPrimaryColorHex);

      const variantSize = sizeData.size || "";
      const variantStyleSize = sizeData.styleSize || "";

      // Images
      const images = variantData.imageURLs || [];
      const validImages = images.filter((img) => img && img.trim() !== "");

      // Web path
      let webPath = [];
      const webLinesArray = productData.categoryParentLinesMap?.Web;
      if (Array.isArray(webLinesArray)) {
        webPath = webLinesArray.map((webLines) => {
          if (!Array.isArray(webLines)) return "";
          const reversed = [...webLines].reverse();
          return reversed
            .map((x) => {
              const t = getCaseInsensitive(x.title, userLang);
              return t || x.id;
            })
            .join(" / ");
        });
      }

      let finalWebCategory = [];

      if (
        productData.webCategoryCode &&
        productData.webCategoryCode.length > 0
      ) {
        finalWebCategory = Array.isArray(productData.webCategoryCode)
          ? productData.webCategoryCode
          : [productData.webCategoryCode];
      } else if (webPath.length > 0) {
        finalWebCategory = webPath;
      }

      // AlliumAI Category
      let alliumCat = "";
      if (productData.alliumInternalData?.processingDetails?.alliumCategory) {
        const catObj =
          productData.alliumInternalData.processingDetails.alliumCategory;
        let catLangKey = "en";
        const lower = selectedLanguage.value.toLowerCase();
        if (lower.startsWith("ger")) catLangKey = "de";
        else if (lower.startsWith("fre")) catLangKey = "fr";
        else if (lower.startsWith("ita")) catLangKey = "it";
        alliumCat = catObj[catLangKey] || "";
      }

      // GPC Brick & Title from categoryParentLinesMap
      let gpcBrickId = "";
      let gpcFullGpcTitle = "";
      const gpcLines = productData.categoryParentLinesMap?.["gpc-0"]?.[0];
      if (Array.isArray(gpcLines) && gpcLines.length > 0) {
        // Extract brick ID from the first element (most specific level - brick)
        const brickLevel = gpcLines[0];
        if (brickLevel) {
          gpcBrickId = brickLevel.id || "";
          gpcFullGpcTitle = getCaseInsensitive(brickLevel.title, userLang) || gpcBrickId;
        }
      }

      // Extract variant-level title if it exists
      const variantTitle = getCaseInsensitive(variantData.title, userLang);
      
      // Check if variant has any of the 3 fields (title, description, bulletPoints)
      // Note: variantDesc is already declared above when checking variant description
      const hasVariantData = !!(
        variantTitle || 
        (variantData.description && getCaseInsensitive(variantData.description, userLang)) || 
        (variantBulletPoints && variantBulletPoints.length > 0)
      );

      return {
        title: titleInLang,
        modelName: modelNameInLang,
        description: descInLang,
        brandName: productData.normalizedBrandName || productData.brandId || "",
        alliumCategory: alliumCat,
        gpcBrickId,
        gpcFullGpcTitle,
        webCategory: finalWebCategory,
        primaryColor: primaryColorLabel || "Unknown",
        primaryColorHexCode: normalizedPrimaryColorHex,
        supplierColor: colorData.supplier_color_name || "",
        alliumInternalData: productData.alliumInternalData || { "N/A": "N/A" },
        allColors: allColorsArray,
        variants: productData.variants,
        images: validImages,
        thumbnails: validImages,
        bulletPoints: productBulletPoints,
        specifications: {
          Size: variantSize,
          ...(variantStyleSize ? { "Style Size": variantStyleSize } : {}),
          ...(productData.normalizedMaterial
            ? {
                "Outer Material": productData.normalizedMaterial.outer || "N/A",
                "Inner Material": productData.normalizedMaterial.inner || "N/A",
                "Filling Material":
                  productData.normalizedMaterial.filling || "N/A",
              }
            : {}),
        },
        materialDeclaration: productData.materialDeclaration || [],
        productAttributes: productData.attributes || {},
        variantAttributes: variantData.attributes || {},
        attributesSources:
          productData.alliumInternalData?.processingDetails?.attributeSources ||
          productData.processingDetails?.attributeSources ||
          {},
        alliumFlaggedAttributes:
          productData.alliumInternalData?.processingDetails?.processingFlags
            ?.attributesFlags || {},
        attributesMatchingDefault: productData.attributesMatchingDefault || {},
        notes: productData.alliumInternalData?.notes || "",
        // Add variant-level data for toggle support
        variantData: {
          title: variantTitle || null,
          description: (variantData.description && getCaseInsensitive(variantData.description, userLang)) || null,
          bulletPoints: variantBulletPoints
        },
        hasVariantData,
        currentVariantId: currentVariantId.value
      };
    });

    // Colors

    const sourceColors = computed(() => {
      const setOfColors = new Map();
      for (const v of currentSourceData?.variants?.value) {
        const cData = v.variantDiscriminators?.color || {};
        let rawVal = cData.rgbColor || cData.colorCode;
        if (!rawVal && Array.isArray(cData.attrValues) && cData.attrValues[0]) {
          rawVal = cData.attrValues[0].value;
        }
        if (!rawVal) continue;
        const displayVal = normalizeHexColor(rawVal) || rawVal;
        setOfColors.set(rawVal, { rawValue: rawVal, displayValue: displayVal });
      }
      return Array.from(setOfColors.values());
    });
    // Sizes

    // Language Buttons
    const languages = computed(() => [
      "english",
      "german",
      "french",
      "italian",
    ]);

    // Nav disabling
    const isPreviousDisabled = computed(() => {
      const index = store.state.filteredProductIds.indexOf(
        currentProductId.value
      );
      return index <= 0;
    });
    const isNextDisabled = computed(() => {
      const index = store.state.filteredProductIds.indexOf(
        currentProductId.value
      );
      return (
        index >= store.state.filteredProductIds.length - 1 &&
        !store.state.hasMoreProducts
      );
    });

    // Lifecycle & watchers
    onMounted(async () => {
      await loadProductData();
      await buildAttributeFilters();
    });

    watch(
      [
        () => props.id,
        () => isSourceCollapsed.value,
        () => selectedSourceOption.value,
      ],
      async (
        [newId, newCollapsed, newSource],
        [oldId, oldCollapsed, oldSource]
      ) => {
        if (newId !== oldId) {
          await loadProductData();
          editedProductAttributes.value = {};
          editedVariantAttributes.value = {};
          editedDetails.value = {};

          const currentIndex = store.state.filteredProductIds.indexOf(newId);
          const totalProducts = store.state.filteredProductIds.length;
          if (
            currentIndex >= totalProducts - 3 &&
            store.state.hasMoreProducts &&
            !store.state.isFetchingMore
          ) {
            await store.preLoadNextBatch();
          }
        }
        if (!newCollapsed && newSource === "Manor Existing" && newId) {
          ensureManorExistingDataFetched();
        }
      }
    );
    watch(
      () => store.state.selectedDatabase,
      async (newDb, oldDb) => {
        if (newDb === oldDb) return;

        // 1) Clear or reset the local 'Manor Existing' cache
        manorExistingCache.value = {};

        // 2) If currently viewing a product, re-load it from the new environment
        if (currentProductId.value) {
          console.log(
            `DB changed from ${oldDb} to ${newDb}. Reloading product...`
          );
          await store.loadProduct(currentProductId.value);

          // Also re-fetch "Manor Existing" data if the panel is open
          if (
            !isSourceCollapsed.value &&
            selectedSourceOption.value === "Manor Existing"
          ) {
            await ensureManorExistingDataFetched();
          }
        }
      }
    );
    const rawStatus = computed(() => {
      return (
        store.getProduct(currentProductId.value)?.alliumInternalData
          ?.processStatus || ""
      );
    });

    // Displayed status (map "Completed" => "Processed")
    const displayedStatus = computed(() => {
      if (rawStatus.value === "Completed") {
        return "Processed";
      }
      return rawStatus.value || "Unknown";
    });

    // Decide which CSS class to apply based on the raw status
    const statusClass = computed(() => {
      switch (rawStatus.value) {
        case "Unprocessed":
          return "status-red";
        case "Processing":
          return "status-blue";
        case "Completed": // even though we display "Processed", the underlying status is "Completed"
          return "status-green";
        default:
          return "status-unknown";
      }
    });
    // Methods
    const loadProductData = async () => {
      if (!currentProductId.value) return;
      if (!currentProductData.value) {
        await store.loadProduct(currentProductId.value);
      }
      selectedVariantId.value = "";
      if (currentProductVariants.value.length > 0) {
        selectedVariantId.value = currentProductVariants.value[0].id;
      }
      selectedColor.value = "";
      selectedSize.value = "";
    };

    const buildAttributeFilters = async () => {
      try {
        for (const category in attributeFilters) {
          attributeFilters[category].attributes = [];
          attributeFilters[category].values = {};
        }
        for (const productId in store.state.products) {
          const product = store.state.products[productId];
          if (product.attributes) {
            processAttributes(product.attributes, "alliumTaxonomy");
          }
          if (product.enhanced_attributes) {
            processAttributes(product.enhanced_attributes, "alliumTaxonomy");
          }
          const dressType = product.attributes?.["20001940"];
          if (dressType) {
            if (
              !attributeFilters.dressCategory.attributes.includes(
                "Dress Category"
              )
            ) {
              attributeFilters.dressCategory.attributes.push("Dress Category");
            }
            if (!attributeFilters.dressCategory.values["Dress Category"]) {
              attributeFilters.dressCategory.values["Dress Category"] =
                new Set();
            }
            attributeFilters.dressCategory.values["Dress Category"].add(
              dressType
            );
          }
          if (product.variants) {
            for (const variant of product.variants) {
              if (variant.attributes) {
                processAttributes(variant.attributes, "alliumTaxonomy");
              }
            }
          }
        }
        for (const cat in attributeFilters) {
          attributeFilters[cat].attributes.sort();
          for (const attr in attributeFilters[cat].values) {
            attributeFilters[cat].values[attr] = Array.from(
              attributeFilters[cat].values[attr]
            ).sort();
          }
        }
      } catch (error) {
        console.error("Error building attribute filters:", error);
      }
    };

    const processAttributes = (attributesObj, category) => {
      for (const [key, attrObj] of Object.entries(attributesObj)) {
        let attrName = key;
        let attrValue = attrObj;
        if (typeof attrObj === "object" && attrObj !== null) {
          attrName = attrObj.attrDisplayName || attrObj.attrName || key;
          if (Array.isArray(attrObj.attrValues) && attrObj.attrValues.length) {
            attrValue = attrObj.attrValues[0].value;
          } else if (attrObj.value) {
            attrValue = attrObj.value;
          }
        }
        if (attrName && attrValue && typeof attrValue === "string") {
          if (!attributeFilters[category].attributes.includes(attrName)) {
            attributeFilters[category].attributes.push(attrName);
            attributeFilters[category].values[attrName] = new Set();
          }
          attributeFilters[category].values[attrName].add(attrValue);
        }
      }
    };

    const setLanguage = (lang) => {
      selectedLanguage.value = lang;
    };

    const emptyProductData = () => {
      return {
        title: "No title available",
        modelName: "",
        description: "No description available.",
        alliumCategory: "",
        gpcBrickId: "",
        gpcFullGpcTitle: "",
        webCategory: "",
        brandName: "",
        supplierColor: "",
        primaryColorHexCode: null,
        primaryColor: "Unknown",
        allColors: [],
        images: [],
        thumbnails: [],
        bulletPoints: [],
        specifications: {},
        materialDeclaration: [],
        productAttributes: {},
        variantAttributes: {},
        attributes: {},
        attributesSources: {},
      };
    };

    // ----------------------------------------------------------
    // Map raw product data (Manor Existing side)
    // ----------------------------------------------------------
    const mapRawProductData = (rawProduct, rawVariant) => {
      console.log('WW!!!#!')
      const userLang = mapLang[selectedLanguage.value] || "EN";

      function fallbackTitleOrDesc(obj) {
        return (
          getCaseInsensitive(obj, "EN") ||
          getCaseInsensitive(obj, "DE") ||
          getCaseInsensitive(obj, "FR") ||
          getCaseInsensitive(obj, "IT")
        );
      }

      // Title
      const rawTitle = getCaseInsensitive(rawProduct.title, userLang);
      const fallbackTitle =
        fallbackTitleOrDesc(rawProduct.title) || "No title available";
      const finalTitle = rawTitle || fallbackTitle;

      // Model Name
      const rawModelName = getCaseInsensitive(rawProduct.modelName, userLang);
      const fallbackModelName = fallbackTitleOrDesc(rawProduct.modelName) || "";
      const finalModelName = rawModelName || fallbackModelName;

      // Product-level description
      const rawDesc = getCaseInsensitive(rawProduct.description, userLang);
      const fallbackDesc =
        fallbackTitleOrDesc(rawProduct.description) ||
        "No description available.";
      const finalDesc = rawDesc || fallbackDesc;

      // Variant-level description (separate from product)
      const variantDesc = getCaseInsensitive(rawVariant.description, userLang);

      // Product-level bullet points
      const productBulletPoints = [];
      if (Array.isArray(rawProduct.bulletPoints)) {
        productBulletPoints.push(...rawProduct.bulletPoints
          .map((point) => point.text?.[userLang])
          .filter(Boolean));
      }
      
      // Variant-level bulletPoints (separate from product)
      const variantBulletPoints = [];
      if (Array.isArray(rawVariant.bulletPoints) && rawVariant.bulletPoints.length > 0) {
        variantBulletPoints.push(...rawVariant.bulletPoints
          .map((point) => point.text?.[userLang])
          .filter(Boolean));
      }

      // Web path
      let webPath = "";
      const webLinesArray = rawProduct.categoryParentLinesMap?.Web;
      if (Array.isArray(webLinesArray)) {
        webPath = webLinesArray.map((webLines) => {
          if (!Array.isArray(webLines)) return "";
          const reversed = [...webLines].reverse();
          return reversed
            .map((x) => {
              const t = getCaseInsensitive(x.title, userLang);
              return t || x.id;
            })
            .join(" / ");
        });
      }

      // GPC path
      let rawGpcPath = "";
      let gpcBrickId = "";
      let gpcFullGpcTitle = "";
      const gpcLines = rawProduct.categoryParentLinesMap?.["gpc-0"]?.[0];
      if (Array.isArray(gpcLines)) {
        const reversedGpc = [...gpcLines].reverse();
        rawGpcPath = reversedGpc
          .map((x) => {
            const t = getCaseInsensitive(x.title, userLang);
            return t || x.id;
          })
          .join("|");
        
        // Extract brick ID from the first element (most specific level - brick)
        if (gpcLines.length > 0) {
          const brickLevel = gpcLines[0];
          if (brickLevel) {
            gpcBrickId = brickLevel.id || "";
            gpcFullGpcTitle = getCaseInsensitive(brickLevel.title, userLang) || gpcBrickId;
          }
        }
      }

      const baseLang = (userLang || "en").toLowerCase();

      const colorData = rawVariant.variantDiscriminators?.color || {};
      const allColorsArray = [];
      if (Array.isArray(colorData.attrValues)) {
        for (const av of colorData.attrValues) {
          allColorsArray.push(av.value || "Unknown");
        }
      }

      let primaryColorName = colorData.colorLabels?.[baseLang];
      if (!primaryColorName) {
        const rawValue = colorData.attrValues?.[0]?.value; // e.g. "blau:0000FF"
        if (rawValue && rawValue.includes(":")) {
          primaryColorName = rawValue.split(":")[0];
        } else {
          primaryColorName = rawValue || "Unknown";
        }
      }

      let primaryColorHex = colorData.rgbColor;
      if (!primaryColorHex) {
        const rawValue = colorData.attrValues?.[0]?.value;
        if (rawValue && rawValue.includes(":")) {
          primaryColorHex = rawValue.split(":")[1];
        } else {
          primaryColorHex = "#CCCCCC";
        }
      }

      const sizeValue = rawVariant.variantDiscriminators?.size?.value || "";
      const styleSizeValue = rawVariant.variantDiscriminators?.size?.styleSize || "";

      let images = [];
      if (Array.isArray(rawVariant.imageURLs)) {
        images = rawVariant.imageURLs;
      } else if (Array.isArray(rawVariant.imageUrls)) {
        images = rawVariant.imageUrls;
      }
      const validImages = images.filter((img) => img && img.trim() !== "");

      // Allium category
      let alliumCat = "";
      if (rawProduct.alliumInternalData?.processingDetails?.alliumCategory) {
        const catObj =
          rawProduct.alliumInternalData.processingDetails.alliumCategory;
        let catLangKey = "en";
        const lower = selectedLanguage.value.toLowerCase();
        if (lower.startsWith("ger")) catLangKey = "de";
        else if (lower.startsWith("fre")) catLangKey = "fr";
        else if (lower.startsWith("ita")) catLangKey = "it";
        alliumCat = catObj[catLangKey] || "";
      }

      // Extract variant-level title if it exists
      const variantTitle = getCaseInsensitive(rawVariant.title, userLang);
      
      // Check if variant has any of the 3 fields (title, description, bulletPoints)
      // Note: variantDesc is already declared above when checking variant description
      const hasVariantData = !!(
        variantTitle || 
        variantDesc || 
        (variantBulletPoints && variantBulletPoints.length > 0)
      );

      return {
        title: finalTitle,
        modelName: finalModelName,
        description: finalDesc,
        alliumCategory: alliumCat,
        gpcBrickId,
        gpcFullGpcTitle,
        webCategory:
          webPath ||
          rawProduct.manorWebCategoryCode ||
          rawProduct.webCategoryCode ||
          "",
        // We'll store the raw Gpc path we just parsed
        rawGpcPath,
        brandName: rawProduct.normalizedBrandName || rawProduct.brandId || "",
        primaryColor: primaryColorName,
        primaryColorHexCode: primaryColorHex,
        supplierColor: colorData.supplier_color_name || "",
        allColors: allColorsArray,
        images: validImages,
        thumbnails: validImages,
        bulletPoints: productBulletPoints,
        specifications: {
          ...(sizeValue ? { Size: sizeValue } : {}),
          ...(styleSizeValue ? { "Style Size": styleSizeValue } : {}),
        },
        materialDeclaration: rawProduct.materialDeclaration || [],
        productAttributes: rawProduct.attributes || {},
        variantAttributes: rawVariant.attributes || {},
        attributes: { ...rawProduct.attributes, ...rawVariant.attributes },
        attributesSources: rawProduct.attribute_sources || {},
        variants: rawProduct.variants,
        // Add variant-level data for toggle support
        variantData: {
          title: variantTitle || null,
          description: variantDesc || null,
          bulletPoints: variantBulletPoints
        },
        hasVariantData,
        currentVariantId: rawVariant.id || ""
      };
    };

    const ensureManorExistingDataFetched = async () => {
      if (!currentProductId.value) return;
      if (manorExistingCache.value[currentProductId.value] !== undefined) {
        return;
      }
      try {
        const rawProd = await productService.fetchRawProductById(
          currentProductId.value,
          store.state.selectedDatabase
        );
        manorExistingCache.value[currentProductId.value] = rawProd || null;
      } catch (err) {
        console.error("Error fetching raw product data:", err);
        manorExistingCache.value[currentProductId.value] = null;
      }
    };

    const toggleSourceSection = () => {
      isSourceCollapsed.value = !isSourceCollapsed.value;
      if (
        !isSourceCollapsed.value &&
        selectedSourceOption.value === "Manor Existing"
      ) {
        ensureManorExistingDataFetched();
      }
    };

    const goToCatalog = () => {
      router.push("/catalog");
    };

    const previousProduct = async () => {
      const currentIndex = store.state.filteredProductIds.indexOf(
        currentProductId.value
      );
      if (currentIndex > 0) {
        const prevId = store.state.filteredProductIds[currentIndex - 1];
        router.push(`/product/${prevId}`);
      }
    };

    const nextProduct = async () => {
      const currentIndex = store.state.filteredProductIds.indexOf(
        currentProductId.value
      );
      if (currentIndex < store.state.filteredProductIds.length - 1) {
        const nextId = store.state.filteredProductIds[currentIndex + 1];
        router.push(`/product/${nextId}`);
      } else if (store.state.hasMoreProducts && !store.state.isFetchingMore) {
        showFeedback("Loading more products...", "success");
        try {
          const loaded = await store.preLoadNextBatch();
          if (loaded) {
            const newIndex = store.state.filteredProductIds.indexOf(
              currentProductId.value
            );
            if (newIndex < store.state.filteredProductIds.length - 1) {
              const nxtId = store.state.filteredProductIds[newIndex + 1];
              router.push(`/product/${nxtId}`);
            } else {
              showFeedback("No more products available", "error");
            }
          } else {
            showFeedback("No more products available", "error");
          }
        } catch (err) {
          console.error("Error loading next batch:", err);
          showFeedback("Error loading next products", "error");
        }
      } else {
        showFeedback("No more products available", "error");
      }
    };

    // Article / Variant / EAN searches
    const onArticleSearch = async () => {
      const article = articleSearchInput.value.trim();
      if (!article) return;
      try {
        store.setIsLoading(true);
        const knownProduct = store.getProduct(article);
        if (knownProduct) {
          router.push(`/product/${article}`);
          return;
        }
        const product = await productService.fetchProcessedProductByArticleId(
          article,
          store.state.selectedDatabase
        );
        if (!product) {
          showFeedback("Article not found", "error");
          return;
        }
        await store.addProductToState(product);
        router.push(`/product/${article}`);
      } catch (err) {
        console.error("Error searching for article:", err);
        showFeedback("Error searching for article", "error");
      } finally {
        store.setIsLoading(false);
      }
    };

    const onVariantSearch = async () => {
      const variantNo = variantSearchInput.value.trim();
      if (!variantNo) return;
      try {
        store.setIsLoading(true);
        // Search in local store
        for (const pid in store.state.products) {
          const product = store.state.products[pid];
          if (product.variants) {
            const found = product.variants.find((v) => v.id === variantNo);
            if (found) {
              router.push(`/product/${pid}`);
              selectedVariantId.value = variantNo;
              return;
            }
          }
        }
        // Otherwise fetch from server
        const product = await productService.fetchProcessedProductByVariantId(
          variantNo,
          store.state.selectedDatabase
        );
        if (!product) {
          showFeedback("Variant not found", "error");
          return;
        }
        await store.addProductToState(product);
        router.push(`/product/${product.id}`);
        const variant = product.variants?.find((v) => v.id === variantNo);
        if (variant) {
          selectedVariantId.value = variantNo;
        }
      } catch (err) {
        console.error("Error searching for variant:", err);
        showFeedback("Error searching for variant", "error");
      } finally {
        store.setIsLoading(false);
      }
    };

    const onEanSearch = async () => {
      const ean = eanSearchInput.value.trim();
      if (!ean) return;
      try {
        store.setIsLoading(true);
        // 1) Look up in local store
        for (const pid in store.state.products) {
          const product = store.state.products[pid];
          if (product.variants) {
            const found = product.variants.find((v) => v.ean === ean);
            if (found) {
              router.push(`/product/${pid}`);
              selectedVariantId.value = found.id;
              return;
            }
          }
        }
        // 2) Otherwise fetch from server
        const product = await productService.fetchProcessedProductByEan(
          ean,
          store.state.selectedDatabase
        );
        if (!product) {
          showFeedback("EAN not found", "error");
          return;
        }
        await store.addProductToState(product);
        router.push(`/product/${product.id}`);
        const variant = product.variants?.find((v) => v.ean === ean);
        if (variant) {
          selectedVariantId.value = variant.id;
        }
      } catch (err) {
        console.error("Error searching for EAN:", err);
        showFeedback("Error searching for EAN", "error");
      } finally {
        store.setIsLoading(false);
      }
    };

    // Color / Size selection
    const selectColor = (c) => {
      selectedColor.value = c;
      updateVariantBySelection();
      editedProductAttributes.value = {};
      editedVariantAttributes.value = {};
      editedDetails.value = {};
    };
    const selectSize = (s) => {
      selectedSize.value = s;
      updateVariantBySelection();
      editedProductAttributes.value = {};
      editedVariantAttributes.value = {};
      editedDetails.value = {};
    };
    const updateVariantBySelection = () => {
      if (!currentProductVariants.value.length) return;
      for (const v of currentProductVariants.value) {
        const colorData = v.variantDiscriminators?.color || {};
        let colorVal = colorData.rgbColor || colorData.colorCode;
        if (
          !colorVal &&
          Array.isArray(colorData.attrValues) &&
          colorData.attrValues[0]
        ) {
          colorVal = colorData.attrValues[0].value;
        }
        const colorMatch =
          !selectedColor.value || colorVal === selectedColor.value;

        let sizeMatch = true;
        if (selectedSize.value) {
          const sz = v.variantDiscriminators?.size?.size || "";
          sizeMatch = sz === selectedSize.value;
        }
        if (colorMatch && sizeMatch) {
          selectedVariantId.value = v.id;
          return;
        }
      }
      // fallback
      selectedVariantId.value = currentProductVariants.value[0].id;
    };

    // Filtering
    const onAttributeNameChange = () => {
      selectedAttributeFilter.value = "";
      applyAttributeFilter();
    };
    const applyAttributeFilter = () => {
      console.log("Apply attribute filter:", selectedAttributeFilter);
    };

    // Notes popup
    const openAddNotePopup = () => {
      showAddNotePopup.value = true;
      reviewNote.value =
        currentProductData.value?.alliumInternalData?.notes || "";
    };
    const closeAddNotePopup = () => {
      showAddNotePopup.value = false;
      reviewNote.value = "";
    };
    const saveNote = async () => {
      if (!currentProductData.value) return;
      try {
        currentProductData.value.alliumInternalData =
          currentProductData.value.alliumInternalData || {};
        currentProductData.value.alliumInternalData.notes = reviewNote.value;

        await productService.saveNote(
          currentProductData.value._id,
          reviewNote.value,
          store.state.selectedDatabase
        );
        showFeedback("Note saved successfully!", "success");
      } catch (err) {
        console.error("Exception while saving note:", err);
        showFeedback("Failed to save note!", "error");
      }
      closeAddNotePopup();
    };

    // Handling attribute edits
    const handleBothAttributeEdited = (
      { key, value },
      includeProductAttributes,
      includeVariantAttributes
    ) => {
      const mergedOriginals = {
        ...(includeProductAttributes &&
          currentEnrichedData.value.productAttributes),
        ...(includeVariantAttributes &&
          currentEnrichedData.value.variantAttributes),
      };
      if (includeProductAttributes)
        var currentAttr =
          editedProductAttributes.value[key] || mergedOriginals[key];
      else
        var currentAttr =
          editedVariantAttributes.value[key] || mergedOriginals[key];

      const newAttributeObject =
        typeof currentAttr === "object" &&
        currentAttr !== null &&
        !Array.isArray(currentAttr)
          ? JSON.parse(JSON.stringify(currentAttr))
          : {};

      if (!newAttributeObject.attrName) {
        newAttributeObject.attrName = key;
      }

      // If `value` is an array, check if it's an array of rich objects or simple strings
      if (Array.isArray(value)) {
        // Check the first element to determine the type of array
        const isRichObjectArray =
          value.length > 0 &&
          typeof value[0] === "object" &&
          value[0] !== null &&
          value[0].valueLabels;
        if (isRichObjectArray) {
          // It's from a multi-select dropdown, so the values are already structured
          newAttributeObject.attrValues = value;
        } else {
          // It's an array of simple strings (e.g., from a multi-value text input)
          newAttributeObject.attrValues = value.map((v) => ({ value: v }));
        }
      }
      // If `value` is a single rich object from our dropdown
      else if (
        typeof value === "object" &&
        value !== null &&
        value.valueLabels
      ) {
        // It's a single selection from a dropdown. The value is the rich object itself.
        newAttributeObject.attrValues = [value];
      }
      // Otherwise, it's a simple primitive value (e.g., from a text input)
      else {
        newAttributeObject.attrValues = [{ value: value }];
      }

      delete newAttributeObject.value;

      if (includeProductAttributes)
        editedProductAttributes.value = {
          ...editedProductAttributes.value,
          [key]: newAttributeObject,
        };
      else
        editedVariantAttributes.value = {
          ...editedVariantAttributes.value,
          [key]: newAttributeObject,
        };
    };

    const handleProductAttributeEdited = ({ key, value }) => {
      handleBothAttributeEdited({ key, value }, true, false);
    };

    const handleVariantAttributeEdited = ({ key, value }) => {
      handleBothAttributeEdited({ key, value }, false, true);
    };

    const handleProductAttributeDeleted = (key) => {
      const updated = { ...editedProductAttributes.value };
      updated[key] = undefined;
      editedProductAttributes.value = updated;
    };

    const handleVariantAttributeDeleted = (key) => {
      const updated = { ...editedVariantAttributes.value };
      updated[key] = undefined;
      editedVariantAttributes.value = updated;
    };

    // Handling detail edits (title, brand, etc.)
    const handleDetailEdited = ({ field, value, isVariantLevel, variantId }) => {
      editedDetails.value = { ...editedDetails.value, [field]: value };
      if (!currentProductData.value) return;

      const langCode = mapLang[selectedLanguage.value] || "EN";
      const variant = currentProductVariants.value.find(
        (v) => v.id === (variantId || currentVariantId.value)
      );

      switch (field) {
        case "title":
          // Handle variant-level or product-level title
          if (isVariantLevel && variant) {
            if (!variant.title) variant.title = {};
            variant.title[langCode] = value;
          } else {
            if (!currentProductData.value.title)
              currentProductData.value.title = {};
            currentProductData.value.title[langCode] = value;
          }
          break;
        case "modelName":
          if (!currentProductData.value.modelName) {
            currentProductData.value.modelName = {};
          }
          currentProductData.value.modelName[langCode] = value;
          break;
        case "description":
          // Handle variant-level or product-level description
          if (isVariantLevel && variant) {
            if (!variant.description) {
              variant.description = {};
            }
            variant.description[langCode] = value;
          } else {
            if (!currentProductData.value.description) {
              currentProductData.value.description = {};
            }
            currentProductData.value.description[langCode] = value;
          }
          break;
        case "bulletPoints": {
          // Handle variant-level or product-level bullet points
          if (isVariantLevel && variant) {
            if (!variant.bulletPoints) {
              variant.bulletPoints = [];
            }
            // Convert the array of strings to the expected format with text objects
            variant.bulletPoints = value.map((text) => ({
              text: { [langCode]: text }
            }));
          } else {
            if (!currentProductData.value.bulletPoints) {
              currentProductData.value.bulletPoints = [];
            }
            currentProductData.value.bulletPoints = value.map((text) => ({
              text: { [langCode]: text }
            }));
          }
          break;
        }
        case "brandName":
          currentProductData.value.normalizedBrandName = value;
          break;
        case "alliumCategory": {
          if (!currentProductData.value.alliumInternalData) {
            currentProductData.value.alliumInternalData = {};
          }
          if (!currentProductData.value.alliumInternalData.processingDetails) {
            currentProductData.value.alliumInternalData.processingDetails = {};
          }
          if (
            !currentProductData.value.alliumInternalData.processingDetails
              .alliumCategory
          ) {
            currentProductData.value.alliumInternalData.processingDetails.alliumCategory =
              {};
          }
          let catLang = "en";
          const lower = selectedLanguage.value.toLowerCase();
          if (lower.startsWith("ger")) catLang = "de";
          else if (lower.startsWith("fre")) catLang = "fr";
          else if (lower.startsWith("ita")) catLang = "it";
          currentProductData.value.alliumInternalData.processingDetails.alliumCategory[
            catLang
          ] = value;
          break;
        }
        case "webCategory":
          currentProductData.value.webCategoryCode = value;
          break;
        case "primaryColor":
          if (variant?.variantDiscriminators?.color) {
            variant.variantDiscriminators.color.colorCode = value;
          }
          break;
        case "primaryColorHexCode":
          if (variant?.variantDiscriminators?.color) {
            variant.variantDiscriminators.color.rgbColor = value;
          }
          break;
        case "supplierColor":
          if (variant?.variantDiscriminators?.color) {
            variant.variantDiscriminators.color.supplier_color_name = value;
          }
          break;
        case "allColors":
          if (variant?.variantDiscriminators?.color) {
            variant.variantDiscriminators.color.all_colors = value;
          }
          break;
        case "specifications": {
          const specs = value || {};
          if (variant?.variantDiscriminators?.size && specs["Size"]) {
            variant.variantDiscriminators.size.size = specs["Size"];
          }
          if (variant?.variantDiscriminators?.size && specs["Style Size"]) {
            variant.variantDiscriminators.size.styleSize = specs["Style Size"];
          }
          if (!currentProductData.value.normalizedMaterial) {
            currentProductData.value.normalizedMaterial = {
              outer: null,
              inner: null,
              filling: null,
            };
          }
          if (specs["Outer Material"]) {
            currentProductData.value.normalizedMaterial.outer =
              specs["Outer Material"];
          }
          if (specs["Inner Material"]) {
            currentProductData.value.normalizedMaterial.inner =
              specs["Inner Material"];
          }
          if (specs["Filling Material"]) {
            currentProductData.value.normalizedMaterial.filling =
              specs["Filling Material"];
          }
          break;
        }
      }
    };

    // Save changes
    const saveChanges = async () => {
      if (!store.getIsAuthenticated() || !currentProductData.value) return;
      const productToSave = JSON.parse(
        JSON.stringify(currentProductData.value)
      );
      productToSave.dbName = store.state.selectedDatabase;

      // Apply editedProductAttributes
      for (const [key, val] of Object.entries(editedProductAttributes.value)) {
        if (val === undefined) {
          if (productToSave.attributes?.[key]) {
            delete productToSave.attributes[key];
          }
        } else {
          if (!productToSave.attributes) productToSave.attributes = {};
          productToSave.attributes[key] = val;
        }
      }

      // Apply editedVariantAttributes to the current variant
      if (Object.keys(editedVariantAttributes.value).length > 0) {
        const variantIndex = productToSave.variants?.findIndex(
          (v) => v.id === currentVariantId.value
        );
        if (variantIndex !== undefined && variantIndex >= 0) {
          const variant = productToSave.variants[variantIndex];
          if (!variant.attributes) variant.attributes = {};
          
          for (const [key, val] of Object.entries(editedVariantAttributes.value)) {
            if (val === undefined) {
              delete variant.attributes[key];
            } else {
              variant.attributes[key] = val;
            }
          }
        }
      }

      try {
        await store.saveProduct(productToSave, auth.currentUser.email);
        showFeedback("Save Successful!", "success");
        editedProductAttributes.value = {};
        editedVariantAttributes.value = {};
        editedDetails.value = {};
      } catch (error) {
        console.error("Exception while saving:", error);
        showFeedback("Save Failed!", "error");
      }
    };

    // Show feedback in snackbar
    const showFeedback = (msg, color = "success") => {
      snackbarMessage.value = msg;
      snackbarColor.value = color;
      showSnackbar.value = true;
      setTimeout(() => {
        showSnackbar.value = false;
      }, 2000);
    };

    // ADDED refreshData() method
    const refreshData = async () => {
      try {
        showFeedback("Refreshing data...", "success");
        // Force-reload the current product from the DB
        await store.loadProduct(currentProductId.value);
        showFeedback("Data refreshed!", "success");
      } catch (err) {
        console.error("Error refreshing data:", err);
        showFeedback("Error refreshing data", "error");
      }
    };

    // NEW: open the confirm popup for reprocessing
    const openReprocessConfirm = () => {
      showReprocessConfirm.value = true;
    };

    const reprocessProduct = async () => {
      try {
        showReprocessConfirm.value = false;
        if (!currentProductData.value || !store.getIsAuthenticated()) return;

        currentProductData.value.alliumInternalData =
          currentProductData.value.alliumInternalData || {};

        currentProductData.value.alliumInternalData.processStatus = "Reprocess";

        if (selectedReprocessOptions.value.length > 0) {
          currentProductData.value.alliumInternalData.partialProcessing = {
            reprocess: selectedReprocessOptions.value,
          };
        } else {
          delete currentProductData.value.alliumInternalData.partialProcessing;
        }

        await store.saveProduct(currentProductData.value, auth.currentUser.email);
        showFeedback("Product queued for reprocessing!", "success");
      } catch (err) {
        console.error("Exception while reprocessing:", err);
        showFeedback("Failed to reprocess!", "error");
      } finally {
        selectedReprocessOptions.value = [];
      }
    };

    return {
      store,
      showAddNotePopup,
      showReprocessConfirm,
      showSnackbar,
      snackbarMessage,
      snackbarColor,
      reviewNote,
      selectedLanguage,
      isSourceCollapsed,
      selectedVariantId,
      selectedColor,
      selectedSize,
      editedProductAttributes,
      editedVariantAttributes,
      editedDetails,
      sourceOptions,
      reprocessOptions,
      selectedSourceOption,
      selectedReprocessOptions,
      manorExistingCache,
      attributeTaxonomies,
      selectedAttributeFilter,
      attributeFilters,
      articleSearchInput,
      variantSearchInput,
      eanSearchInput,

      statusColor,
      statusIcon,
      statusText,
      formattedDate,

      qualityCheckColor,
      qualityCheckLabel,

      currentProductId,
      currentProductData,
      currentProductVariants,
      currentVariantId,
      currentVariantData,
      currentSourceData,
      currentSourceDataFound,
      currentEnrichedData,
      languages,
      isPreviousDisabled,
      isNextDisabled,

      topLevelFlags,

      loadProductData,
      buildAttributeFilters,
      processAttributes,
      setLanguage,
      toggleSourceSection,
      goToCatalog,
      previousProduct,
      nextProduct,
      onArticleSearch,
      onVariantSearch,
      onEanSearch,
      selectColor,
      selectSize,
      updateVariantBySelection,
      onAttributeNameChange,
      applyAttributeFilter,
      openAddNotePopup,
      closeAddNotePopup,
      saveNote,
      handleBothAttributeEdited,
      handleProductAttributeEdited,
      handleVariantAttributeEdited,
      handleProductAttributeDeleted,
      handleVariantAttributeDeleted,
      handleDetailEdited,
      saveChanges,
      showFeedback,
      shouldHighlightGpc,
      refreshData,
      openReprocessConfirm,
      reprocessProduct,
      displayedStatus,
      statusClass,
    };
  },
};
</script>

<style scoped>
.product-view {
  padding: 20px;
}

/* Top controls */
.top-product-controls {
  display: flex;
  align-items: center;
  justify-content: space-between; /* pushes left-controls left & right-controls right */
  margin-bottom: 10px;
  padding: 0 20px; /* optional horizontal padding */
}

.center-controls {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.language-buttons {
  display: flex;
  gap: 10px;
}

.language-buttons button {
  background-color: #34495e;
  color: #fff;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
}

.language-buttons button:hover {
  background-color: #3e5571;
}

.language-buttons .selected-lang {
  background-color: #e67e22;
  color: #fff;
}

.save-button {
  background-color: #2c3e50;
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
}

.save-button:hover {
  background-color: #34495e;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #f3f3f3;
  color: #2c3e50;
  border: 1px solid #ced4da;
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
}

.back-button svg {
  stroke: #2c3e50; /* Match your text color */
}

.back-button:hover {
  background-color: #e2e6ea; /* Slightly darker hover */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* Right panel container */
.right-panel {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
}

/* Refresh button */
.refresh-button {
  background-color: #5c6bc0;
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
  margin-top: 10px;
}
.refresh-button:hover {
  background-color: #3949ab;
}

/* Notes */
.notes-button {
  background-color: #5c6bc0;
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
  margin-top: 10px;
}
.notes-button:hover {
  background-color: #3949ab;
}
.notes-button.has-notes {
  background-color: #f39c12;
  color: #fff;
}

/* NEW: Reprocess button */
.reprocess-button {
  background-color: #e74c3c;
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
  margin-top: 10px;
}
.reprocess-button:hover {
  background-color: #c0392b;
}

/* Modal (Notes & Reprocess Popup) */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 800px;
}
.modal-buttons {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Loading state */
.loading-state {
  text-align: center;
  margin-top: 100px;
  font-size: 20px;
  color: #2c3e50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
}
.loading-spinner {
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #2c3e50;
  animation: spin 0.8s ease-in-out infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Main layout */
.images-and-controls {
  display: flex;
}
.images-container {
  width: 500px;
  flex-shrink: 0;
  padding: 15px;
  box-sizing: border-box;
  background-color: #fff;
  border-right: 1px solid #bdc3c7;
}
.page-navigation {
  margin-top: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.navigation-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.article-number-text {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}
.variant-number-text {
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50;
  margin-top: 5px;
  text-align: center;
}
.ean-text {
  font-size: 14px;
  margin-top: 2px;
  color: #555;
}
.web-link {
  margin-top: 5px;
  text-align: center;
}
.web-link a {
  color: #e67e22;
  text-decoration: underline;
  font-size: 16px;
}
.nav-button {
  background-color: #2c3e50;
  border: none;
  padding: 8px;
  margin: 0 10px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s, box-shadow 0.3s;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.nav-button svg {
  width: 24px;
  height: 24px;
  stroke: #fff;
}
.nav-button:hover:not(:disabled) {
  background-color: #34495e;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
.nav-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
.nav-button:disabled svg {
  stroke: #666;
}

/* Attribute filters + searches */
.attribute-filter {
  margin-top: 20px;
  text-align: center;
}
.attribute-filter label {
  margin-right: 10px;
  font-weight: bold;
}
.article-search,
.variant-search,
.ean-search {
  margin-top: 20px;
  text-align: center;
}

/* Content layout */
.content-container {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  max-height: calc(100vh - 90px);
}
.comparison-container {
  display: flex;
  flex-direction: row;
  gap: 30px;
  margin-bottom: 50px;
}
.comparison-section {
  flex: 1;
  background-color: #fff;
  padding: 30px;
  border-radius: 15px;
  box-sizing: border-box;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}
.section-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #2c3e50;
  text-align: center;
}
.panel {
  margin-bottom: 30px;
  overflow-x: auto;
}
.two-pane-container {
  display: flex;
  gap: 20px;
  margin-bottom: 50px;
}
.left-pane {
  flex: 2.5;
  background-color: #fff;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}
.right-pane {
  flex: 1;
  max-width: 50%;
  background-color: #fff;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}
.collapse-button {
  margin: 10px 0 20px 20px;
  background: #eee;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
  height: 36px;
}
.collapse-button:hover {
  background: #ddd;
}

/* Snackbar */
.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
.snackbar.success {
  background: #28a745;
}
.snackbar.error {
  background: #dc3545;
}
.right-controls {
  display: flex;
  align-items: center;
  /* Add gap here: */
  gap: 12px;
}
.process-status {
  padding: 6px 10px;
  border-radius: 4px;
  font-weight: bold;
  color: #fff;
  font-size: 14px;
}

.quality-flag-container {
  display: flex;
  flex-direction: column;
}
.quality-flag-display {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 150px;
}

.status-red {
  background-color: #e74c3c; /* red */
}
.status-blue {
  background-color: #3498db; /* blue */
}
.status-green {
  background-color: #2ecc71; /* green */
}
.status-unknown {
  background-color: #7f8c8d; /* gray */
}
.status-orange {
  background-color: #f8c25c; /* orange */
}
.status-text-red {
  color: #FF6962; /* red */
}
.status-text-green {
  color: #77DD76; /* red */
}
</style>
